import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AppProvider } from './context/AppContext';
import { AlertProvider } from './context/AlertContext';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './pages/Login';
import './App.css';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Lazy load pages for better performance
const Monitoring = lazy(() => import('./pages/Monitoring'));
const Charts = lazy(() => import('./pages/Charts'));
const Settings = lazy(() => import('./pages/Settings'));
const History = lazy(() => import('./pages/History'));
const Speeding = lazy(() => import('./pages/Speeding'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Alerts = lazy(() => import('./pages/Alerts'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// Root redirect component
const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  return isAuthenticated ? <Navigate to="/monitoring" replace /> : <Navigate to="/login" replace />;
};

// App Routes component (needs to be inside AuthProvider)
const AppRoutes = () => {
  return (
    <Routes>
      {/* Root - redirects based on auth status */}
      <Route path="/privacy-policy"  element={<PrivacyPolicy/>}/>
      {/* Public Route - Login */}
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<RootRedirect />} />
      {/* Protected Routes */}
      <Route
        path="/monitoring"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Monitoring />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/charts"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Charts />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Settings />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <History />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/speeding"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Speeding />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requiredMenu="users">
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <UserManagement />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute requiredMenu="alert">
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Alerts />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* 404 Page */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Layout>
              <div className="card">
                <div className="text-center py-12">
                  <h1 className="text-6xl font-bold text-gray-900 dark:text-white mb-4">
                    404
                  </h1>
                  <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                    Page not found
                  </p>
                  <a href="/monitoring" className="btn-primary">
                    Go to Dashboard
                  </a>
                </div>
              </div>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppProvider>
              <AlertProvider>
                <AppRoutes />
              </AlertProvider>
            </AppProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
